import { useEffect, useMemo, useState } from "react";
import { HelpCircle, PlayCircle, RotateCcw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { useOnboardingTour, type TourStatus } from "@/hooks/useOnboardingTour";
import { TOUR_DEFINITIONS, type TourName } from "./tours";
import { OnboardingTooltip } from "./OnboardingTooltip";

function getStatusLabel(status: TourStatus) {
  if (status === "completed") {
    return "Completo";
  }

  if (status === "dismissed") {
    return "Dispensado";
  }

  if (status === "in_progress") {
    return "Em andamento";
  }

  return "Não visto";
}

function getStatusClasses(status: TourStatus) {
  if (status === "completed") {
    return "bg-emerald-500/10 text-emerald-600 border-emerald-500/20";
  }

  if (status === "dismissed") {
    return "bg-orange-500/10 text-orange-600 border-orange-500/20";
  }

  if (status === "in_progress") {
    return "bg-blue-500/10 text-blue-600 border-blue-500/20";
  }

  return "bg-muted text-muted-foreground";
}

export function OnboardingTour() {
  const isMobile = useIsMobile();
  const {
    activeTourName,
    currentTourName,
    currentStep,
    currentStepIndex,
    totalSteps,
    isActive,
    allTourNames,
    startTour,
    nextStep,
    prevStep,
    closeTour,
    resetTour,
    getTourStatus,
  } = useOnboardingTour();
  const [targetRect, setTargetRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [allToursOpen, setAllToursOpen] = useState(false);

  useEffect(() => {
    if (!currentStep) {
      setTargetRect(null);
      return;
    }

    const updateRect = () => {
      const element = document.querySelector<HTMLElement>(currentStep.selector);

      if (!element) {
        setTargetRect(null);
        return;
      }

      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    };

    updateRect();

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [currentStep]);

  const currentTourDefinition = useMemo(
    () => (activeTourName ? TOUR_DEFINITIONS[activeTourName] : null),
    [activeTourName],
  );

  const handleRestartCurrentTour = () => {
    if (!currentTourName) {
      return;
    }

    resetTour(currentTourName);
    startTour(currentTourName);
  };

  const handleStartTour = (tourName: TourName) => {
    resetTour(tourName);
    startTour(tourName);
    setAllToursOpen(false);
  };

  return (
    <>
      <OnboardingTooltip
        open={isActive && !!currentStep}
        title={currentStep?.title ?? currentTourDefinition?.title ?? ""}
        description={currentStep?.description ?? ""}
        currentStep={currentStepIndex + 1}
        totalSteps={totalSteps}
        targetRect={targetRect}
        isMobile={isMobile}
        onPrev={prevStep}
        onNext={nextStep}
        onClose={closeTour}
      />

      <div className="fixed bottom-24 right-6 z-[110]">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              className="h-12 w-12 rounded-full bg-[#4A42A8] text-white shadow-xl hover:bg-[#3f3891]"
            >
              <HelpCircle className="h-5 w-5" />
              <span className="sr-only">Ajuda</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={handleRestartCurrentTour} disabled={!currentTourName}>
              Ver tour desta tela
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAllToursOpen(true)}>
              Ver todos os tours
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={allToursOpen} onOpenChange={setAllToursOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Todos os tours</DialogTitle>
            <DialogDescription>
              Reative qualquer tour quando quiser e acompanhe o progresso por tela.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            {allTourNames.map((tourName) => {
              const definition = TOUR_DEFINITIONS[tourName];
              const status = getTourStatus(tourName);

              return (
                <div
                  key={tourName}
                  className="flex flex-col gap-3 rounded-2xl border border-border/60 bg-card/50 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground">{definition.title}</h3>
                      <Badge variant="outline" className={getStatusClasses(status)}>
                        {getStatusLabel(status)}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {definition.steps.length} passos · rota {definition.route}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => resetTour(tourName)}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Resetar
                    </Button>
                    <Button size="sm" onClick={() => handleStartTour(tourName)}>
                      <PlayCircle className="mr-2 h-4 w-4" />
                      Ver tour
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
